import React, { useCallback, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { PracticeProviderInfo } from "../../../redux/slice/practice/practiceProvidersSlice";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { AddCircleOutline, Cancel, Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useModalStyles } from "../../common/useModalStyles";

interface ProviderProps {
  open: boolean;
  onClose: () => void;
  addProvider: (provider: PracticeProviderInfo) => void;
  onChange: (name: string, value: string) => void;
  practiceProviderObject: PracticeProviderInfo;
  billingType?: string;
}

export const providerInitialState: any = {
  providerId: undefined,
  practiceId: undefined,
  title: "",
  firstName: "",
  lastName: "",
  middleName: "",
  npi: "",
  ptan: "",
  taxId: "",
  email: "",
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const ProviderPopup: React.FC<ProviderProps> = ({
  open,
  onClose,
  addProvider,
  onChange,
  practiceProviderObject,
  billingType,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, primaryBtnSx, closeIconSx, cancelBtnSx, fieldWrapSx } = useModalStyles();

  const { control, handleSubmit, reset, watch, formState: { errors } } =
    useForm<PracticeProviderInfo>();

  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [personTitleList, setPersonTitleList] = React.useState<CodeTable[]>([]);

  const personTitleListOptions = useMemo(() => {
    return personTitleList
      .map((item) => ({ label: item.displayValue, value: item.keyName }))
      .sort((a, b) =>
        a.value === "OTHER" ? 1 : b.value === "OTHER" ? -1 : 0
      );
  }, [personTitleList]);

  React.useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      setPersonTitleList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PersonTitle")
      );
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (open) reset(practiceProviderObject);
    if (!open) setDefaultValues();
  }, [open]);

  const setDefaultValues = useCallback(() => {
    reset();
  }, []);

  const onSubmit = (data: PracticeProviderInfo) => {
    const dataToSubmit = {
      ...data,
      title:
        data.title === "OTHER" ? data.otherTitle || "" : data.title || "",
    };
    addProvider(dataToSubmit);
    reset(providerInitialState);
    onClose();
  };

  const isEditing = !!practiceProviderObject?.firstName;
  const isGroupBilling = billingType === "GROUP";

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.88rem",
        fontWeight: 600,
        color: tokens.textPrimary,
        mb: 0.8,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.2,
            borderBottom: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
          }}
        >
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary }}
          >
            {isEditing ? "Edit Provider" : "Add Provider"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={closeIconSx}>
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        {/* ── Body ── */}
        <DialogContent
          sx={{ px: 3, py: 3, overflowY: "auto", flex: 1, minHeight: 0 }}
        >
          <Grid container spacing={2}>

            {/* Row 1 — Title (narrow) + First Name (wide) */}
            <Grid item xs={4} md={3}>
              <FieldLabel label="Title" />
              <Box sx={fieldWrapSx()}>
              <CustomDropdownField
                control={control}
                options={personTitleListOptions}
                name="title"
                sortOptions={false}
                size="small"
                placeholderText="Title"
              />
              </Box>
            </Grid>

            <Grid item xs={8} md={9}>
              <FieldLabel label="First Name" required />
              <Box sx={fieldWrapSx(!!errors?.firstName)}>
              <CustomTextField
                control={control}
                controllerName="firstName"
                placeholder="First name"
                size="small"
                rules={{
                  required: "First Name is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (value?.trim() === "") return "First Name is required.";
                    },
                  },
                }}
              />
              </Box>
            </Grid>

            {/* Specified Title — only when OTHER selected */}
            {watch("title") === "OTHER" && (
              <Grid item xs={12} md={6}>
                <FieldLabel label="Specified Title" required />
                <Box sx={fieldWrapSx(!!errors?.otherTitle)}>
                <CustomTextField
                  control={control}
                  controllerName="otherTitle"
                  placeholder="Specified title"
                  maxLength={10}
                  size="small"
                  rules={{ required: "Specified title is required." }}
                />
                </Box>
              </Grid>
            )}

            {/* Row 2 — Middle Name + Last Name */}
            <Grid item xs={12} md={6}>
              <FieldLabel label="Middle Name" />
              <Box sx={fieldWrapSx()}>
              <CustomTextField
                control={control}
                controllerName="middleName"
                placeholder="Middle name"
                size="small"
              />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel label="Last Name" required />
              <Box sx={fieldWrapSx(!!errors?.lastName)}>
              <CustomTextField
                control={control}
                controllerName="lastName"
                placeholder="Last name"
                size="small"
                rules={{
                  required: "Last Name is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (value?.trim() === "") return "Last Name is required.";
                    },
                  },
                }}
              />
              </Box>
            </Grid>

            {/* Row 3 — NPI + PTAN */}
            <Grid item xs={12} md={6}>
              <FieldLabel label="NPI" required={!isGroupBilling} />
              <Box sx={fieldWrapSx(!!errors?.npi)}>
              <CustomTextField
                control={control}
                controllerName="npi"
                placeholder="10-digit NPI"
                maxLength={10}
                size="small"
                rules={
                  isGroupBilling
                    ? {}
                    : {
                        required: "NPI is required.",
                        validate: {
                          customValidation: (value: string) => {
                            if (value?.trim() === "") return "NPI is required.";
                          },
                        },
                      }
                }
              />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel label="PTAN" required={!isGroupBilling} />
              <Box sx={fieldWrapSx(!!errors?.ptan)}>
              <CustomTextField
                control={control}
                controllerName="ptan"
                placeholder="PTAN"
                maxLength={10}
                size="small"
                rules={
                  isGroupBilling
                    ? {}
                    : {
                        required: "PTAN is required.",
                        validate: {
                          customValidation: (value: string) => {
                            if (value?.trim() === "") return "PTAN is required.";
                          },
                        },
                      }
                }
              />
              </Box>
            </Grid>

            {/* Row 4 — Tax ID + Email */}
            <Grid item xs={12} md={6}>
              <FieldLabel label="Tax ID" />
              <Box sx={fieldWrapSx()}>
              <CustomTextField
                control={control}
                controllerName="taxId"
                placeholder="XX-XXXXXXX"
                maxLength={10}
                size="small"
              />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FieldLabel label="Email" />
              <Box sx={fieldWrapSx(!!errors?.email)}>
              <CustomTextField
                control={control}
                controllerName="email"
                placeholder="email@example.com"
                size="small"
                rules={{
                  validate: (value: string) => {
                    if (!value) return true;
                    return (
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
                        value
                      ) || "Invalid email address."
                    );
                  },
                }}
              />
              </Box>
            </Grid>

          </Grid>
        </DialogContent>

        {/* ── Footer ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={
              <AddCircleOutline sx={{ fontSize: "1rem !important" }} />
            }
            sx={primaryBtnSx}
          >
            {isEditing ? "Update Provider" : "Add Provider"}
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default ProviderPopup;